/**
 * Centralised round/zone colour resolution.
 *
 * The user can edit each round's colour in the Rounds tab (saved in DB on the
 * round record). This utility loads those colours from the API and exposes a
 * resolver so badges across Work Planner, Job List, Customer cards etc all use
 * the SAME colour the user picked — not the stale hardcoded `zoneColors` map.
 *
 * Resolution order: dynamic (DB) > legacy hardcoded map (zoneColors) > grey fallback.
 */
import { zoneColors } from '../data/csvData';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_COLOR = '#6b7280';

// Module-level cache. Populated lazily on first use and on user-triggered refresh.
let dynamicRoundColors = {};
let loadPromise = null;
const subscribers = new Set();

const notify = () => subscribers.forEach((cb) => {
  try { cb(); } catch (e) { /* swallow */ }
});

/**
 * Resolve a round/zone colour by exact name, then case-insensitive fallback.
 */
export const getRoundColor = (name) => {
  if (!name) return zoneColors.default || FALLBACK_COLOR;
  if (dynamicRoundColors[name]) return dynamicRoundColors[name];
  // case-insensitive fallback inside dynamic map
  const lower = name.toLowerCase();
  for (const key of Object.keys(dynamicRoundColors)) {
    if (key.toLowerCase() === lower) return dynamicRoundColors[key];
  }
  // Legacy hardcoded map (so untracked legacy zones still get a colour)
  if (zoneColors[name]) return zoneColors[name];
  return zoneColors.default || FALLBACK_COLOR;
};

/**
 * Fetch rounds from the API and update the cache. Idempotent and de-duped — calling
 * during an in-flight load returns the same promise.
 */
export const loadRoundColors = async () => {
  if (loadPromise) return loadPromise;
  const token = localStorage.getItem('streakfree_token');
  if (!token) return null;
  loadPromise = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/rounds`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const rounds = Array.isArray(data) ? data : (data.rounds || []);
      const map = {};
      rounds.forEach((r) => {
        if (r && r.name && r.color) map[r.name] = r.color;
      });
      dynamicRoundColors = map;
      notify();
      return map;
    } catch (err) {
      console.warn('[roundColors] Failed to load rounds:', err);
      return null;
    } finally {
      loadPromise = null;
    }
  })();
  return loadPromise;
};

/**
 * Subscribe to colour-map changes so components can re-render when the user
 * updates a round's colour in another tab/page.
 */
export const subscribeToRoundColors = (cb) => {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
};

export const refreshRoundColors = () => {
  loadPromise = null;
  return loadRoundColors();
};
